import React, {type ReactNode, type RefObject} from 'react'
import {type ReplyRun, replyBlocks} from '@/Pages/Events/Assistant/assistantTranscript'

interface Props {
    text: string
    className?: string
    testId?: string
    hidden?: boolean
    tabIndex?: number
    nodeRef?: RefObject<HTMLElement | null>
}

const linkClass = 'break-all text-royal-10 underline underline-offset-2 focus-visible:rounded-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-royal-20'

const Runs = ({runs, keyAt}: {runs: ReplyRun[], keyAt: (text: string) => string}): ReactNode => (
    <>
        {runs.map((run) => {
            const key = keyAt(run.text)
            const body = run.bold ? <strong className="font-semibold">{run.text}</strong> : run.text

            if (run.href === undefined) {
                return <span key={key}>{body}</span>
            }

            return (
                <a key={key} href={run.href} target="_blank" rel="noreferrer" className={linkClass}>
                    {body}
                </a>
            )
        })}
    </>
)

const FormattedText = ({
    text,
    className,
    testId,
    hidden = false,
    tabIndex,
    nodeRef,
}: Props): ReactNode => {
    let cursor = 0
    const keyAt = (value: string): string => {
        const key = String(cursor)
        cursor += value.length + 1

        return key
    }

    return (
        <div
            ref={nodeRef as RefObject<HTMLDivElement | null>}
            tabIndex={tabIndex}
            data-testid={testId}
            aria-hidden={hidden ? true : undefined}
            className={className}
        >
            <div className="space-y-2">
                {replyBlocks(text).map((block) => block.type === 'list' ? (
                    <ul key={keyAt('list')} className="list-disc space-y-1 pl-5">
                        {block.items.map((item) => (
                            <li key={keyAt(item.map((run) => run.text).join(''))}><Runs runs={item} keyAt={keyAt}/></li>
                        ))}
                    </ul>
                ) : (
                    <p key={keyAt('paragraph')} className="whitespace-pre-wrap">
                        {block.lines.map((line, lineIndex) => (
                            <span key={keyAt(line.map((run) => run.text).join(''))}>
                                {/* A text newline stays in textContent. A br does not, so the copied-line break would vanish. */}
                                {lineIndex > 0 ? '\n' : null}
                                <Runs runs={line} keyAt={keyAt}/>
                            </span>
                        ))}
                    </p>
                ))}
            </div>
        </div>
    )
}

export default FormattedText
